import { useState } from 'react';
import { useSelector, useDispatch } from '_common/hooks';
import { FormattedMessage } from 'react-intl';
import { Dropdown, Divider } from '@ets/design-system';
import { SplitToggle } from '_common/components';

import EditorManager from 'Editor/services/EditorManager';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { selectReadOnlyMode, selectIsPageLayout } from 'Editor/redux/EditorStatusSlice';
import { getNumberedList } from 'Editor/redux/listStylesSlice';
import { selectedNumberedList } from 'Editor/redux/ToolbarSlice';
import PreviewLists from 'Editor/components/PreviewLists/PreviewLists';

import styles from './NumberedList.module.scss';

const NumberedList = () => {
  const dispatch = useDispatch();
  const selectedList = useSelector((state) => state.editor.toolbar.selectedList);
  const selected = useSelector((state) => selectedNumberedList(state));
  const numberedLists = useSelector((state) => getNumberedList(state));
  const isReadOnlyMode = useSelector((state) => selectReadOnlyMode(state));
  const isPageLayout = useSelector(selectIsPageLayout);
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleList = (element: string) => {
    const manager = EditorManager.getInstance();
    if (element === selectedList) {
      manager.toggleList(false);
    } else {
      manager.toggleList(element);
    }
  };

  const handleListAction = (customize: boolean) => {
    dispatch(
      openAndUpdateModal({
        modal: 'NewListModal',
        data: {
          customize,
          type: 'numbered',
        },
      }),
    );
  };

  return (
    <Dropdown.Root
      open={isOpen}
      onOpenChange={() => {
        if (!isReadOnlyMode) setIsOpen(!isOpen);
      }}
    >
      <Dropdown.Trigger>
        <SplitToggle
          isToggled={selected || isOpen}
          icon="list-ol"
          iconColor="neutral"
          isOpen={selected || isOpen}
          disabled={isReadOnlyMode || isPageLayout}
          testId="numbered-list"
          margin="2px"
          onClick={() => {
            handleToggleList(selectedList);
          }}
          onPopperClick={() => setIsOpen(!isOpen)}
        />
      </Dropdown.Trigger>
      <Dropdown.Content
        align="end"
        style={{
          width: Object.keys(numberedLists).length > 6 ? '32.5rem' : '30rem',
          paddingTop: 0,
        }}
        testId="numbered-list-dropdown"
      >
        <div className={styles.styles}>
          {Object.keys(numberedLists).map((element) => (
            <div
              key={element}
              onClick={() => {
                handleToggleList(element);
                setIsOpen(false);
              }}
              className={`${styles.item} ${element === selectedList && styles.selected}`}
              data-testid={`${element}-list`}
            >
              <PreviewLists list={numberedLists[element]} />
            </div>
          ))}
        </div>
        <Divider margin="0.25rem 0 0.25rem 0" />
        {Object.keys(numberedLists).length > 0 && (
          <Dropdown.Item
            onClick={() => handleListAction(true)}
            testId="numbered-list-customize-list-styles"
          >
            <FormattedMessage id="CUSTOMIZE_LIST_STYLES" />
          </Dropdown.Item>
        )}
        <Dropdown.Item
          onClick={() => handleListAction(false)}
          testId="numbered-list-create-new-list"
        >
          <FormattedMessage id="CREATE_NEW_LIST" />
        </Dropdown.Item>
      </Dropdown.Content>
    </Dropdown.Root>
  );
};

export default NumberedList;
